import { createContext, FC, ReactNode, useCallback, useContext, useState } from 'react';
import { ExternalCatalogResponse } from 'src/services/catalog_products/useGetExternalCatalogMutation';
import PunchoutErrorDialog from './PunchoutErrorDialog';
import { usePunchoutExternalSiteRedirect } from './usePunchoutRedirect';

interface PunchoutRedirectContextValue {
  openPunchout: (catalogId: number) => Promise<void>;
  isOpeningPunchout: boolean;
  openingCatalogId?: number;
}

const PunchoutRedirectContext = createContext<PunchoutRedirectContextValue | null>(null);

interface PunchoutRedirectProviderProps {
  errorMessage: string;
  children: ReactNode;
}

export const PunchoutRedirectProvider: FC<PunchoutRedirectProviderProps> = ({
  errorMessage,
  children,
}) => {
  const [punchoutError, setPunchoutError] = useState<ExternalCatalogResponse | null>(null);
  const { openPunchout, isPending, openingCatalogId } = usePunchoutExternalSiteRedirect({
    errorMessage,
    onFailure: setPunchoutError,
  });

  const closePunchoutError = useCallback(() => {
    setPunchoutError(null);
  }, []);

  return (
    <PunchoutRedirectContext.Provider
      value={{
        openPunchout,
        isOpeningPunchout: isPending,
        openingCatalogId,
      }}
    >
      {children}
      <PunchoutErrorDialog response={punchoutError} onClose={closePunchoutError} />
    </PunchoutRedirectContext.Provider>
  );
};

export const usePunchoutRedirectContext = () => {
  const context = useContext(PunchoutRedirectContext);
  if (!context) {
    throw new Error('usePunchoutRedirectContext must be used within PunchoutRedirectProvider');
  }
  return context;
};

export default PunchoutRedirectContext;
